/**
 * ==========================================================
 * Aurora Geriátrica
 * © 2026 Luis Fernando Chacón Cordón
 * Guatemala
 * Software propietario - todos los derechos reservados
 * ==========================================================
 */

import AdminSettings from "./pages/admin/AdminSettings";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Landing from "./pages/Landing";
import LandingUS from "./pages/LandingUS";
import LandingNueva from "./pages/LandingNueva";
import Privacidad from "./pages/Privacidad";
import Terminos from "./pages/Terminos";
import DatosClinicos from "./pages/DatosClinicos";
import Precios from "./pages/Precios";
import Marketing from "./pages/Marketing";
import DemoHome from "./pages/DemoHome";
import EntrarDemo from "./pages/EntrarDemo";
import DemoConsultation from "./pages/DemoConsultation";
import DemoDocument from "./pages/DemoDocument";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import CheckoutRedirect from "./pages/CheckoutRedirect";
import Dashboard from "./pages/Dashboard";
import DashboardVisualV1 from "./pages/DashboardVisualV1";
import ClinicalAgendaV1 from "./pages/ClinicalAgendaV1";
import Patients from "./pages/Patients";
import PatientProfile from "./pages/PatientProfile";
import PatientDashboardV1 from "./pages/PatientDashboardV1";
import NewConsultation from "./pages/NewConsultation";
import ConsultationEditRedirect from "./components/ConsultationEditRedirect";
import ConsultationView from "./pages/ConsultationView";
import GenerarEntregablePage from "./pages/GenerarEntregablePage";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import BillingRequired from "./pages/BillingRequired";
import Reports from "./pages/Reports";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import AulaLayout from "./layouts/AulaLayout";
import InstitucionLayout from "./layouts/InstitucionLayout";
import AulaDashboard from "./pages/aula/AulaDashboard";
import AulaCasos from "./pages/aula/AulaCasos";
import AulaCasoDetalle from "./pages/aula/AulaCasoDetalle";
import AulaNuevaPractica from "./pages/aula/AulaNuevaPractica";
import AulaMisPracticas from "./pages/aula/AulaMisPracticas";
import AulaPracticaDetalle from "./pages/aula/AulaPracticaDetalle";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminFeedback from "./pages/admin/AdminFeedback";
import AdminAdvertising from "./pages/admin/AdminAdvertising";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminUsageDashboard from "./pages/admin/AdminUsageDashboard";
import AdminAulaUniversidades from "./pages/admin/AdminAulaUniversidades";
import Contact from "./pages/Contact";
import Fundadores from "./pages/Fundadores";
import VgiQuickEvaluation from "./pages/VgiQuickEvaluation";
import ResourcesIndex from "./pages/ResourcesIndex";
import ResourceArticle from "./pages/ResourceArticle";
import { isUsMarketingHost } from "./lib/usMarketHost";
import { isUsClinicalHost } from "./lib/usClinicalHost";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, accessBlocked, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.search.includes("checkout=success")) {
      refreshUser?.().then(() => navigate("/dashboard", { replace: true }));
    }
  }, [location.search, refreshUser, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-sky-50">
        <div className="animate-pulse text-sky-600 font-medium">Cargando Aurora Geriátrica...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (accessBlocked) {
    return <BillingRequired />;
  }

  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-sky-50">
        <div className="animate-pulse text-sky-600 font-medium">Cargando Aurora Geriátrica...</div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

// Guard para Aurora Aula — requiere account_mode === "academic"
const AulaRoute = ({ children }) => {
  const { isAuthenticated, loading, isAula } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAula) return <Navigate to="/dashboard" replace />;
  return children;
};

// Guard para Aurora Institucional — requiere account_mode === "institutional"
const InstitucionRoute = ({ children }) => {
  const { isAuthenticated, loading, isInstitucional } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isInstitucional) return <Navigate to="/dashboard" replace />;
  return children;
};

const isAppDomain = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    h === "app.aurorageriatrica.com" ||
    h === "app.aurorageriatric.com" ||
    process.env.REACT_APP_FORCE_APP_MODE === "true"
  );
};

const AppRootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (!isAppDomain()) {
    if (isUsMarketingHost()) {
      if (loading) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-sky-50">
            <div className="animate-pulse text-sky-600 font-medium">Cargando Aurora Geriátrica...</div>
          </div>
        );
      }
      if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
      }
      return <LandingUS />;
    }
    return <Landing />;
  }
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-sky-50">
        <div className="animate-pulse text-sky-600 font-medium">Cargando Aurora Geriátrica...</div>
      </div>
    );
  }
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
};

const NewConsultationWrapper = () => {
  const location = useLocation();
  const modoAula = new URLSearchParams(location.search).get("modo") === "aula";
  return (
    <Layout hideSidebar={modoAula}>
      <NewConsultation />
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <HelmetProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppRootRedirect />} />
          <Route path="/us" element={<LandingUS />} />
          <Route path="/landing-nueva" element={<LandingNueva />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Login defaultTab="register" />} />
          <Route path="/register/aula" element={<Login defaultTab="register" modoAula={true} />} />
          <Route path="/checkout" element={<CheckoutRedirect />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/datos" element={<DatosClinicos />} />
          <Route path="/contacto" element={<Contact />} />
          <Route path="/fundadores" element={<Fundadores />} />
          <Route path="/precios" element={<Precios />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/recursos" element={<ResourcesIndex />} />
          <Route path="/recursos/:slug" element={<ResourceArticle />} />
          <Route path="/demo-clinico" element={<DemoHome />} />
          <Route path="/entrar-demo" element={<EntrarDemo />} />
          <Route path="/demo-clinico/consulta" element={<DemoConsultation />} />
          <Route path="/demo-clinico/documento" element={<DemoDocument />} />
          <Route path="/demo/caratula-visual" element={<DashboardVisualV1 showcaseMode={true} />} />
          <Route path="/demo/agenda-visual" element={<ClinicalAgendaV1 showcaseMode={true} />} />
          <Route
            path="/admin/*"
            element={
              <AdminProtectedRoute>
                <ErrorBoundary>
                  <AdminLayout>
                    <Routes>
                      <Route index element={<AdminDashboard />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="aula" element={<AdminAulaUniversidades />} />
                      <Route path="billing" element={<AdminBilling />} />
                      <Route path="contacts" element={<AdminContacts />} />
                      <Route path="feedback" element={<AdminFeedback />} />
                      <Route path="advertising" element={<AdminAdvertising />} />
                      <Route path="analytics" element={<AdminAnalytics />} />
                      <Route path="uso" element={<AdminUsageDashboard />} />
                      <Route path="settings" element={<AdminSettings />} />
                    </Routes>
                  </AdminLayout>
                </ErrorBoundary>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/aula/*"
            element={
              <AulaRoute>
                <AulaLayout>
                  <Routes>
                    <Route path="dashboard" element={<AulaDashboard />} />
                    <Route path="casos" element={<AulaCasos />} />
                    <Route path="casos/:patient_id" element={<AulaCasoDetalle />} />
                    <Route path="practicas" element={<AulaMisPracticas />} />
                    <Route path="practicas/nueva" element={<AulaNuevaPractica />} />
                    <Route path="practicas/:consultation_id" element={<AulaPracticaDetalle />} />
                  </Routes>
                </AulaLayout>
              </AulaRoute>
            }
          />
          <Route
            path="/institucion/*"
            element={
              <InstitucionRoute>
                <InstitucionLayout>
                  <Routes>
                    <Route path="dashboard" element={<div>Aurora Institucional — próximamente</div>} />
                  </Routes>
                </InstitucionLayout>
              </InstitucionRoute>
            }
          />
          <Route
            path="/nueva-consulta"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <NewConsultationWrapper />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/panel/visual-v1" element={<DashboardVisualV1 />} />
                      <Route path="/pacientes" element={<Patients />} />
                      <Route path="/pacientes/:id/dashboard-v1" element={<PatientDashboardV1 />} />
                      <Route path="/pacientes/:id" element={<PatientProfile />} />
                      <Route path="/pacientes/:id/editar" element={<Patients />} />
                      <Route path="/nueva-consulta/:patientId" element={<NewConsultation />} />
                      <Route path="/consulta/:id/editar" element={<ConsultationEditRedirect />} />
                      <Route path="/consulta/:id" element={<ConsultationView />} />
                      <Route path="/entregable/:id" element={<GenerarEntregablePage />} />
                      <Route path="/agenda" element={<ClinicalAgendaV1 />} />
                      <Route path="/reportes" element={<Reports />} />
                      <Route
                        path="/vgi-rapida"
                        element={isUsClinicalHost() ? <VgiQuickEvaluation /> : <Navigate to="/dashboard" replace />}
                      />
                      <Route path="/configuracion" element={<Settings />} />
                      <Route path="/configuracion/billing" element={<Billing />} />
                    </Routes>
                  </Layout>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors />
        </BrowserRouter>
      </HelmetProvider>
    </AuthProvider>
  );
}

export default App;
